import type { ReactElement } from 'react'

import type { PoemFigure as Figure } from './scene-art.ts'
import { TWEMOJI_GRID } from './twemoji-shape.ts'

/** Where `build-poem-art.ts` vendors the artwork, served by Vite from `public/`. */
export const ART_PATH = '/art'

export function figureSource(figure: Figure): string {
  return `${ART_PATH}/${figure.codePoint}.svg`
}

export interface PoemFigureProps {
  readonly figure: Figure
  /**
   * Set on the first figure of a document only — the one that may be in the
   * first viewport. Everything below the fold stays lazy.
   */
  readonly priority?: boolean
  /** Placement class for the plate; stanza figures take the default. */
  readonly className?: string
}

/**
 * One vendored icon, as a file rather than as geometry.
 *
 * AN `<img>`, NOT INLINE SVG, WHICH REVERSES PHASE 0016 DELIBERATELY. That phase
 * inlined the geometry for two stated reasons, and both were right for a
 * full-strength emblem fixed in the viewport margin. Neither survived the move
 * into the flow:
 *
 * - *"An `<img>` is an LCP candidate."* It still is, and now that is the right
 *   outcome rather than the wrong one — the candidate is a ~1.6 KB first-party
 *   file on a prerendered page. `priority` is what makes it cheap: the one figure
 *   that might paint first is fetched eagerly at high priority, and the rest are
 *   lazy.
 * - *"An external SVG is a separate fetch under `img-src`."* `img-src 'self'
 *   data:` is already in `STATIC_DIRECTIVES`, so this needs no CSP change. The
 *   fetches are same-origin, individually cacheable, and shared across poems
 *   wherever two poems draw the same thing.
 *
 * THE REASON IS THE BUNDLE, AND IT IS NOT A SMALL ONE. Inline geometry cost 634
 * gzipped bytes per code point in the client bundle, on every route, for every
 * reader — 29.2 KB of 117.6 KB to draw pictures of which any one page showed
 * three. One figure per stanza would have taken that past 47%. As files it costs
 * the bundle nothing and the page only what it actually shows.
 *
 * `alt=""` AND NOTHING ELSE. An empty alt is the canonical decorative marker: it
 * removes the image from the accessibility tree outright, so `aria-hidden` and
 * `role="presentation"` would both be restating it. The figures carry no
 * information the verse does not — that is what makes them safe to alternate
 * against DOM order in `_poems.sass`.
 *
 * `width` and `height` ARE THE INTRINSIC GRID, not the rendered size, which CSS
 * owns. Together with the 1:1 `aspect-ratio` in the stylesheet they are what
 * holds CLS at 0 while the file is still in flight.
 */
export function PoemFigure({
  figure,
  priority = false,
  className = 'cena__figura',
}: PoemFigureProps): ReactElement {
  return (
    <img
      className={className}
      src={figureSource(figure)}
      width={TWEMOJI_GRID}
      height={TWEMOJI_GRID}
      alt=""
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : 'low'}
    />
  )
}
